import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import './Navbar.css';

const Navbar = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Update scroll state
      setIsScrolled(window.scrollY > 50);

      // Update progress bar
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.scrollY / totalScroll) * 100;
      setScrollProgress(currentProgress);

      // Update active section
      const sections = ['home', 'projects', 'experience', 'awards', 'talks', 'chat'];
      let currentSection = sections[0];
      let minDistance = Infinity;

      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          const distance = Math.abs(rect.top);
          if (distance < minDistance) {
            minDistance = distance;
            currentSection = sectionId;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount to set initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-switcher')) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80; // Approximate navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const letterVariants = {
    initial: { y: 20, opacity: 0 },
    animate: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.05,
      },
    }),
  };

  const navItems = [
    { id: 'home', label: t.nav.home },
    { id: 'experience', label: t.nav.experience },
    { id: 'awards', label: t.nav.awards },
    { id: 'projects', label: t.nav.projects },
    { id: 'talks', label: t.nav.talks },
    { id: 'chat', label: t.nav.chat }
  ];

  const handleLanguageButtonClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const handleLanguageChange = (event, langCode) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const savedPosition = window.pageYOffset;
    toggleLanguage(langCode);
    setTimeout(() => {
      window.scrollTo(0, savedPosition);
    }, 0);
    setIsLanguageDropdownOpen(false);
  };

  const handleMoreMenuClick = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  useEffect(() => {
    const handleClickOutsideMore = (event) => {
      if (!event.target.closest('.more-menu')) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutsideMore);
    return () => document.removeEventListener('click', handleClickOutsideMore);
  }, []);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <motion.div
        className="scroll-progress"
        style={{ scaleX: scrollProgress / 100 }}
      />
      <div className="navbar-container">
        {/* Menu de 3 pontinhos - mobile only */}
        <div className="more-menu">
          <button
            className="more-button"
            onClick={handleMoreMenuClick}
            aria-label="Menu de navegação"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1" />
              <circle cx="12" cy="5" r="1" />
              <circle cx="12" cy="19" r="1" />
            </svg>
          </button>
          <AnimatePresence>
            {isMoreMenuOpen && (
              <motion.div
                className="more-menu-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {navItems.map(({ id, label }) => (
                  <button
                    key={id}
                    className="more-menu-item"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(id);
                      setIsMoreMenuOpen(false);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Resto do código da navbar */}
        <motion.a
          href="#home"
          className="nav-logo"
          initial="initial"
          animate="animate"
          whileHover={{ scale: 1.05 }}
          onClick={(e) => {
            e.preventDefault();
            scrollToSection('home');
          }}
        >
          <div className="logo-text">
            {Array.from("RB").map((letter, i) => (
              <motion.span
                key={i}
                variants={letterVariants}
                custom={i}
                className="logo-letter"
              >
                {letter}
              </motion.span>
            ))}
          </div>
        </motion.a>

        <div className="nav-sections">
          {navItems.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`nav-link ${activeSection === id ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(id);
              }}
            >
              {label}
              {id === 'chat' && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="chat-icon"
                  style={{ width: '1em', height: '1em', marginLeft: '0.5em' }}
                >
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              )}
            </a>
          ))}
        </div>

        <div className="nav-controls">
          <div className="language-switcher">
            <button
              type="button"
              className="language-button"
              onClick={handleLanguageButtonClick}
            >
              {languages.find(lang => lang.code === language)?.flag}
              <span className="language-name">{language.toUpperCase()}</span>
              <motion.span
                className="dropdown-arrow"
                animate={{ rotate: isLanguageDropdownOpen ? 180 : 0 }}
              >
                ▼
              </motion.span>
            </button>
            <AnimatePresence>
              {isLanguageDropdownOpen && (
                <motion.div
                  className="language-dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {languages.map((lang) => (
                    <button
                      type="button"
                      key={lang.code}
                      data-lang={lang.code}
                      className={`language-option ${language === lang.code ? 'active' : ''}`}
                      onClick={(e) => handleLanguageChange(e, lang.code)}
                    >
                      {lang.flag} <span>{lang.name}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={isDarkMode ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          >
            {isDarkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
